// M04 角、平行線、三角形與四邊形 - 主數據文件
// Angles, Parallel Lines, Triangles and Quadrilaterals - Main Data File
// 系統化設計，準備後續 Supabase 數據遷移

// 導入分割數據文件
import {
  M04_01_PROBLEMS,
  M04_02_PROBLEMS,
  M04_03_PROBLEMS,
  M04_04_PROBLEMS,
  M04_05_PROBLEMS
} from './M04_Topics_01_to_05.js';

import {
  M04_06_PROBLEMS,
  M04_07_PROBLEMS,
  M04_08_PROBLEMS,
  M04_09_PROBLEMS,
  M04_10_PROBLEMS
} from './M04_Topics_06_to_10.js';

import {
  M04_11_PROBLEMS,
  M04_12_PROBLEMS,
  M04_13_PROBLEMS,
  M04_14_PROBLEMS,
  M04_15_PROBLEMS
} from './M04_Topics_11_to_15.js';

// M04 模組基本信息
export const M04_MODULE_INFO = {
  id: "M04",
  title: "角、平行線、三角形與四邊形",
  titleEn: "Angles, Parallel Lines, Triangles and Quadrilaterals",
  description: "深入學習角的性質、平行線定理、三角形與四邊形的幾何性質，掌握全等與相似的判定方法",
  difficulty: "中級",
  totalTopics: 15,
  totalProblems: 90,
  estimatedHours: 12,
  prerequisites: ["基本幾何概念", "代數基礎", "角度計算"],
  applications: ["幾何證明", "測量學", "建築設計", "工程製圖", "計算機圖形學"],
  learningObjectives: [
    "掌握角的分類和性質",
    "理解平行線的性質和判定",
    "熟練運用三角形和四邊形的性質",
    "掌握全等和相似的判定方法",
    "能夠進行幾何證明和計算",
    "應用幾何知識解決實際問題"
  ]
};

// M04 主題列表
export const M04_TOPICS = [
  {
    id: "M04_01",
    title: "平行線角度關係",
    titleEn: "Parallel Lines Angle Relationships",
    description: "學習平行線被橫截線所截時形成的角度關係和性質",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["同位角", "內錯角", "同側內角", "平行線性質"],
    prerequisites: ["基本角度概念", "平行線定義"]
  },
  {
    id: "M04_02",
    title: "角度性質證明",
    titleEn: "Angle Properties Proof",
    description: "學習角的性質定理及其證明方法",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["角平分線", "對頂角", "補角餘角", "角度證明"],
    prerequisites: ["角的基本概念", "幾何證明基礎"]
  },
  {
    id: "M04_03",
    title: "代數角度求解",
    titleEn: "Algebraic Angle Solutions",
    description: "運用代數方法求解幾何圖形中的未知角度",
    difficulty: 2,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["方程建立", "角度計算", "代數幾何結合"],
    prerequisites: ["代數基礎", "角度性質"]
  },
  {
    id: "M04_04",
    title: "角度比例分配",
    titleEn: "Angle Proportion Distribution",
    description: "學習角度的比例關係和分配計算",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["比例計算", "角度分配", "內角和應用"],
    prerequisites: ["比例概念", "角度計算"]
  },
  {
    id: "M04_05",
    title: "三角形邊長範圍",
    titleEn: "Triangle Side Length Range",
    description: "掌握三角形三邊關係和邊長範圍的判定",
    difficulty: 2,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["三角不等式", "邊長約束", "存在性判定"],
    prerequisites: ["三角形基本概念", "不等式"]
  },
  {
    id: "M04_06",
    title: "三角不等式證明",
    titleEn: "Triangle Inequality Proof",
    description: "學習三角不等式的證明方法和應用",
    difficulty: 4,
    estimatedMinutes: 40,
    problemCount: 6,
    keySkills: ["幾何證明", "不等式證明", "最值問題"],
    prerequisites: ["三角形性質", "證明技巧"]
  },
  {
    id: "M04_07",
    title: "平行四邊形性質",
    titleEn: "Parallelogram Properties",
    description: "深入學習平行四邊形的性質和判定方法",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["對角線性質", "角度關係", "面積計算"],
    prerequisites: ["平行線性質", "四邊形基礎"]
  },
  {
    id: "M04_08",
    title: "特殊四邊形判定",
    titleEn: "Special Quadrilateral Identification",
    description: "學習矩形、菱形、正方形等特殊四邊形的判定",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["四邊形分類", "判定條件", "性質應用"],
    prerequisites: ["平行四邊形", "特殊角度"]
  },
  {
    id: "M04_09",
    title: "菱形對角線關係",
    titleEn: "Rhombus Diagonal Relationships",
    description: "深入研究菱形對角線的特殊性質和應用",
    difficulty: 3,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["對角線垂直", "面積計算", "角度關係"],
    prerequisites: ["菱形性質", "勾股定理"]
  },
  {
    id: "M04_10",
    title: "正多邊形角度",
    titleEn: "Regular Polygon Angles",
    description: "學習正多邊形的角度計算和性質",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["內角外角", "中心角", "對稱性"],
    prerequisites: ["多邊形基礎", "角度計算"]
  },
  {
    id: "M04_11",
    title: "多邊形邊數求解",
    titleEn: "Polygon Side Number Solutions",
    description: "根據角度和對角線信息求解多邊形邊數",
    difficulty: 3,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["邊數計算", "內角和應用", "對角線公式"],
    prerequisites: ["多邊形性質", "方程求解"]
  },
  {
    id: "M04_12",
    title: "內角和證明",
    titleEn: "Interior Angle Sum Proof",
    description: "學習多邊形內角和定理的證明方法",
    difficulty: 4,
    estimatedMinutes: 40,
    problemCount: 6,
    keySkills: ["數學歸納法", "幾何證明", "三角剖分"],
    prerequisites: ["證明技巧", "邏輯推理"]
  },
  {
    id: "M04_13",
    title: "全等條件證明",
    titleEn: "Congruence Condition Proof",
    description: "掌握三角形全等的判定條件和證明方法",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["SSS", "SAS", "ASA", "AAS", "HL"],
    prerequisites: ["三角形性質", "幾何證明"]
  },
  {
    id: "M04_14",
    title: "相似三角形判定",
    titleEn: "Similar Triangle Identification",
    description: "學習三角形相似的判定方法和性質應用",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["AA相似", "SAS相似", "SSS相似", "比例關係"],
    prerequisites: ["三角形性質", "比例概念"]
  },
  {
    id: "M04_15",
    title: "面積比例關係",
    titleEn: "Area Proportion Relationships",
    description: "掌握相似圖形和幾何分割中的面積比例關係",
    difficulty: 3,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["面積比", "相似比", "幾何分割"],
    prerequisites: ["面積計算", "相似性質"]
  }
];

// 合併所有問題數據
export const M04_PROBLEMS_DATA = {
  "M04_01": M04_01_PROBLEMS,
  "M04_02": M04_02_PROBLEMS,
  "M04_03": M04_03_PROBLEMS,
  "M04_04": M04_04_PROBLEMS,
  "M04_05": M04_05_PROBLEMS,
  "M04_06": M04_06_PROBLEMS,
  "M04_07": M04_07_PROBLEMS,
  "M04_08": M04_08_PROBLEMS,
  "M04_09": M04_09_PROBLEMS,
  "M04_10": M04_10_PROBLEMS,
  "M04_11": M04_11_PROBLEMS,
  "M04_12": M04_12_PROBLEMS,
  "M04_13": M04_13_PROBLEMS,
  "M04_14": M04_14_PROBLEMS,
  "M04_15": M04_15_PROBLEMS
};

// 輔助函數：根據主題ID獲取問題
export const getProblemsByTopicId = (topicId) => {
  return M04_PROBLEMS_DATA[topicId] || [];
};

// 輔助函數：獲取所有M04問題
export const getAllM04Problems = () => {
  return Object.values(M04_PROBLEMS_DATA).flat();
};

// 輔助函數：根據難度獲取問題
export const getProblemsByDifficulty = (difficulty) => {
  return getAllM04Problems().filter(problem => problem.difficulty === difficulty);
};

// 輔助函數：獲取主題信息
export const getTopicById = (topicId) => {
  return M04_TOPICS.find(topic => topic.id === topicId);
};

// 輔助函數：搜索問題
export const searchProblems = (keyword) => {
  const allProblems = getAllM04Problems();
  return allProblems.filter(problem =>
    problem.question.includes(keyword) ||
    problem.explanation.includes(keyword) ||
    problem.hint.includes(keyword)
  );
};

// M04 統計信息
export const M04_STATISTICS = {
  totalProblems: getAllM04Problems().length,
  totalTopics: M04_TOPICS.length,
  difficultyDistribution: {
    level1: getProblemsByDifficulty(1).length,
    level2: getProblemsByDifficulty(2).length,
    level3: getProblemsByDifficulty(3).length,
    level4: getProblemsByDifficulty(4).length
  },
  averageDifficulty: (
    getAllM04Problems().reduce((sum, problem) => sum + problem.difficulty, 0) /
    getAllM04Problems().length
  ).toFixed(1),
  estimatedStudyTime: M04_TOPICS.reduce((sum, topic) => sum + topic.estimatedMinutes, 0),
  topicsByDifficulty: {
    beginner: M04_TOPICS.filter(topic => topic.difficulty === 1).length,
    intermediate: M04_TOPICS.filter(topic => topic.difficulty === 2).length,
    advanced: M04_TOPICS.filter(topic => topic.difficulty === 3).length,
    expert: M04_TOPICS.filter(topic => topic.difficulty === 4).length
  },
  keyFeatures: [
    "完整覆蓋15個核心主題",
    "系統化90題練習",
    "多層次難度設計",
    "理論與應用並重",
    "涵蓋基礎到高級內容",
    "實際問題應用豐富"
  ],
  topicBreakdown: {
    "基礎角度理論": ["M04_01", "M04_02", "M04_03", "M04_04"],
    "三角形性質": ["M04_05", "M04_06", "M04_13", "M04_14"],
    "四邊形幾何": ["M04_07", "M04_08", "M04_09"],
    "多邊形理論": ["M04_10", "M04_11", "M04_12"],
    "面積與比例": ["M04_15"]
  },
  practicalApplications: [
    "建築設計中的角度計算",
    "工程製圖的幾何關係",
    "測量學中的三角測量",
    "計算機圖形學的幾何變換",
    "藝術設計的比例美學",
    "物理學中的力學分析"
  ]
};

// 數據驗證函數
export const validateM04Data = () => {
  const issues = [];

  // 檢查每個主題的問題數量
  M04_TOPICS.forEach(topic => {
    const problems = getProblemsByTopicId(topic.id);
    if (problems.length !== 6) {
      issues.push(`主題 ${topic.id} 問題數量不正確: ${problems.length}/6`);
    }
  });

  // 檢查問題ID唯一性
  const allProblems = getAllM04Problems();
  const ids = allProblems.map(p => p.id);
  const uniqueIds = [...new Set(ids)];
  if (ids.length !== uniqueIds.length) {
    issues.push('存在重複的問題ID');
  }

  // 檢查必需字段
  allProblems.forEach(problem => {
    const requiredFields = ['id', 'topicId', 'question', 'solution', 'explanation', 'hint', 'difficulty', 'order', 'steps'];
    requiredFields.forEach(field => {
      if (!problem[field]) {
        issues.push(`問題 ${problem.id} 缺少字段: ${field}`);
      }
    });
  });

  // 檢查topicId一致性
  Object.keys(M04_PROBLEMS_DATA).forEach(topicId => {
    const problems = M04_PROBLEMS_DATA[topicId];
    problems.forEach(problem => {
      if (problem.topicId !== topicId) {
        issues.push(`問題 ${problem.id} 的topicId不匹配: ${problem.topicId} vs ${topicId}`);
      }
    });
  });

  return {
    isValid: issues.length === 0,
    issues,
    summary: {
      totalProblems: allProblems.length,
      totalTopics: M04_TOPICS.length,
      expectedProblems: M04_TOPICS.length * 6,
      validationTime: new Date().toISOString(),
      dataIntegrity: {
        uniqueIds: ids.length === uniqueIds.length,
        completeFields: issues.filter(issue => issue.includes('缺少字段')).length === 0,
        correctCounts: issues.filter(issue => issue.includes('問題數量不正確')).length === 0
      }
    }
  };
};

// 學習路徑推薦函數
export const getRecommendedLearningPath = (userLevel = 'beginner') => {
  const paths = {
    beginner: [
      'M04_01', 'M04_03', 'M04_04', 'M04_05', 'M04_10', 
      'M04_07', 'M04_08', 'M04_13', 'M04_14', 'M04_15'
    ],
    intermediate: [
      'M04_02', 'M04_06', 'M04_09', 'M04_11', 'M04_12',
      'M04_01', 'M04_03', 'M04_07', 'M04_13', 'M04_14'
    ],
    advanced: [
      'M04_06', 'M04_12', 'M04_02', 'M04_09', 'M04_11',
      'M04_13', 'M04_14', 'M04_15', 'M04_08', 'M04_10'
    ]
  };

  return paths[userLevel] || paths.beginner;
};

// 主題依賴關係
export const M04_DEPENDENCIES = {
  'M04_01': [],
  'M04_02': ['M04_01'],
  'M04_03': ['M04_01', 'M04_02'],
  'M04_04': ['M04_01', 'M04_03'],
  'M04_05': [],
  'M04_06': ['M04_05'],
  'M04_07': ['M04_01'],
  'M04_08': ['M04_07'],
  'M04_09': ['M04_08'],
  'M04_10': ['M04_01'],
  'M04_11': ['M04_10'],
  'M04_12': ['M04_11'],
  'M04_13': ['M04_05'],
  'M04_14': ['M04_13'],
  'M04_15': ['M04_14']
};

// 導出所有數據和函數
export default {
  M04_MODULE_INFO,
  M04_TOPICS,
  M04_PROBLEMS_DATA,
  getAllM04Problems,
  getProblemsByTopicId,
  getProblemsByDifficulty,
  getTopicById,
  searchProblems,
  M04_STATISTICS,
  validateM04Data,
  getRecommendedLearningPath,
  M04_DEPENDENCIES
}; 